<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = useRouter()
const authStore = useAuthStore()

const isLoggedIn = computed(() => authStore.isLoggedIn)

function handleJoin() {
  router.push('/register')
}

function goTo(path) {
  router.push(path)
}
</script>

<template>
  <div class="home-page">
    <!-- 未登录：招募引导 -->
    <template v-if="!isLoggedIn">
      <section class="hero">
        <div class="container hero-content">
          <h1>客户资源换<span class="highlight">长期销售收益</span></h1>
          <p class="hero-desc">
            有客户资源？加入水芯平台，用免费样品推动客户测试，成交回款后按规则结算佣金提成。
          </p>
          <div class="hero-actions">
            <button class="cta-btn" @click="handleJoin">立即加入</button>
            <router-link to="/join" class="link-more">了解详情 →</router-link>
          </div>
        </div>
      </section>

      <section class="quick-info">
        <div class="container info-grid">
          <div class="info-card">
            <span class="info-icon">💰</span>
            <h3>约 10% 佣金中位数</h3>
            <p>回款后按规则结算</p>
          </div>
          <div class="info-card">
            <span class="info-icon">📦</span>
            <h3>免费样品</h3>
            <p>样品测试推动真实成交</p>
          </div>
          <div class="info-card">
            <span class="info-icon">⏰</span>
            <h3>兼职即可</h3>
            <p>不影响现有工作，时间自由</p>
          </div>
        </div>
      </section>

      <section class="cta-section">
        <div class="container">
          <router-link to="/applications" class="browse-link">先看看有哪些产品 →</router-link>
        </div>
      </section>
    </template>

    <!-- 已登录：工作台 -->
    <template v-else>
      <section class="workbench">
        <div class="container">
          <h2 class="welcome">你好，{{ authStore.nickname || authStore.phone || '业务员' }}</h2>

          <div class="quick-actions">
            <div class="action-card" @click="goTo('/applications')">
              <span class="action-icon">📋</span>
              <span class="action-text">浏览产品</span>
            </div>
            <div class="action-card" @click="goTo('/sample/apply')">
              <span class="action-icon">📦</span>
              <span class="action-text">申请样品</span>
            </div>
            <div class="action-card" @click="goTo('/commission/calc')">
              <span class="action-icon">💰</span>
              <span class="action-text">佣金计算</span>
            </div>
            <div class="action-card" @click="goTo('/sample/list')">
              <span class="action-icon">📄</span>
              <span class="action-text">样品记录</span>
            </div>
            <div class="action-card" @click="goTo('/earnings')">
              <span class="action-icon">📊</span>
              <span class="action-text">我的收益</span>
            </div>
            <div class="action-card" @click="goTo('/customers')">
              <span class="action-icon">👥</span>
              <span class="action-text">我的客户</span>
            </div>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<style scoped>
.home-page {
  min-height: calc(100vh - 140px);
}

/* 未登录 Hero */
.hero {
  background: linear-gradient(135deg, #0a2e5c 0%, #005eb8 100%);
  color: white;
  padding: 80px 20px;
  text-align: center;
}

.hero h1 {
  font-size: 42px;
  font-weight: 800;
  margin: 0 0 16px;
}

.highlight {
  color: #ffd600;
  font-size: 48px;
}

.hero-desc {
  font-size: 17px;
  opacity: 0.9;
  margin: 0 0 32px;
}

.hero-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
}

.cta-btn {
  padding: 14px 40px;
  background: #ffd600;
  color: #0a2e5c;
  font-size: 16px;
  font-weight: 700;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s;
}

.cta-btn:hover {
  transform: translateY(-2px);
}

.link-more {
  color: rgba(255,255,255,0.8);
  text-decoration: none;
  font-size: 14px;
}

.link-more:hover {
  color: white;
}

/* Quick info cards */
.quick-info {
  padding: 60px 20px;
  background: #f8f9fa;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  max-width: 800px;
  margin: 0 auto;
}

.info-card {
  text-align: center;
  padding: 24px;
  background: white;
  border-radius: 12px;
}

.info-icon {
  font-size: 32px;
  display: block;
  margin-bottom: 8px;
}

.info-card h3 {
  font-size: 16px;
  margin: 0 0 6px;
  color: #1a1a1a;
}

.info-card p {
  font-size: 13px;
  color: #666;
  margin: 0;
}

.cta-section {
  padding: 40px 20px;
  text-align: center;
}

.browse-link {
  color: #005eb8;
  font-size: 15px;
  text-decoration: none;
  font-weight: 500;
}

.browse-link:hover {
  text-decoration: underline;
}

/* 已登录工作台 */
.workbench {
  padding: 40px 20px 80px;
  background: #f5f7fa;
}

.welcome {
  font-size: 22px;
  font-weight: 600;
  margin: 0 0 24px;
  color: #1a1a1a;
}

.quick-actions {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.action-card {
  background: white;
  border-radius: 12px;
  padding: 28px 20px;
  text-align: center;
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.2s;
}

.action-card:hover {
  box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  transform: translateY(-2px);
}

.action-icon {
  font-size: 28px;
  display: block;
  margin-bottom: 10px;
}

.action-text {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

@media (max-width: 768px) {
  .hero h1 { font-size: 28px; }
  .highlight { font-size: 34px; }
  .info-grid { grid-template-columns: 1fr; }
  .quick-actions { grid-template-columns: repeat(2, 1fr); }
}
</style>
